import { useContext } from 'react';
import { CellCombinedProps } from './types';
import { TableStoreContext } from '../../../stores/TableStoreContext';
import { MainCellProps } from '../../../lib/types';
import { handleEnter, handleTab } from './utils';
import { useCellState } from '../../../lib/hooks/useCellState';
import { CellLocation } from '../../../stores/types';

const usePresenter = (props: CellCombinedProps): CellCombinedProps => {
  const { row, column } = props;
  const cellLocation: CellLocation = { row, column };
  const tableStore = useContext(TableStoreContext);
  const cellState = tableStore.tableState[row].cellStates[column];

  const {
    getCellSchema,
    getReferenceOptionsData,
    updateCellState,
    updateCellValue,
    uploadAsset,
  } = useCellState(cellLocation);

  const toggleHighlightRow = (selected: boolean) => {
    if (selected) {
      tableStore.setSelectedRow(row);
      tableStore.updateRowState('Highlighted', row);
      tableStore.setHoveredRow(undefined);
      updateCellState('CellFirstSelect');
      return;
    }

    tableStore.setSelectedRow(undefined);
    tableStore.resetRowState(row);
  };

  const handleBlur = () => {
    toggleHighlightRow(false);
  };

  const handleClick = () => {
    toggleHighlightRow(true);
  };

  const handleDoubleClick = () => {
    updateCellState('CellSecondSelect');
  };

  const handleFocus = () => {
    toggleHighlightRow(true);
  };

  // FIXME: inconsistent tabbing for non-textual cells
  // TODO: use the isKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>, options: KeyDownOptions) implementation
  const handleKeyDown = (event?: React.KeyboardEvent<HTMLDivElement | HTMLTextAreaElement>) => {
    if (event && ['Enter', 'Tab'].includes(event.key)) {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleEnter(cellLocation, tableStore);
      } else {
        handleTab(event, cellLocation, tableStore);
      }
    }
  };

  const mainCellProps: MainCellProps = {
    getCellSchema,
    getReferenceOptionsData,
    handleBlur,
    handleClick,
    handleDoubleClick,
    handleFocus,
    handleKeyDown,
    updateCellState,
    updateCellValue,
    uploadAsset,
    dataType: cellState.dataType,
    invalid: cellState.invalid,
    state: cellState.state,
    value: cellState?.value,
  };

  return {
    ...props,
    attachmentContent: { ...mainCellProps },
    codeContent: { ...mainCellProps },
    dateContent: { ...mainCellProps },
    imageContent: { ...mainCellProps },
    longTextContent: { ...mainCellProps },
    multiSelectContent: { ...mainCellProps },
    singleSelectContent: { ...mainCellProps },
    textContent: { ...mainCellProps },
  };
};

export default usePresenter;
